import React, { useState, useEffect, useMemo } from 'react'
import { ReactComponent as HeartIcon } from '../../../assets/svg/HeartIcon.svg'
import { ReactComponent as AddPlusCircleIcon } from '../../../assets/svg/AddPlusCircleIcon.svg'
import { ReactComponent as ChatConversationCircleIcon } from '../../../assets/svg/ChatConversationCircleIcon.svg'
import { ReactComponent as UserCardIDIcon } from '../../../assets/svg/UserCardIDIcon.svg'
import { studentData } from '../../../constants/StudentAnalyticsConstants'
import Loading from '../../Loading'
import { LoadingContainer } from './CheckInWidget'
import { createConversation, loadStudentWellbeingSchoolYEarData } from '../../../server/server'
import WellBeingAsignModal from '../../modals/WellBeingAsignModal'
import WellBeingTakeActionModal from '../../modals/WellBeingTakeActionModal'
import { useUser } from '../../../auth'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import InfoButton from '../../common/InfoButton'

function StudentCardAnalytics({ openStudentInfoModal, studentId, student }) {
    const { t } = useTranslation()
    const { user } = useUser()
    const [monitoring, setMonitoring] = useState(false)
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(studentData)
    const showLoading = useMemo(() => loading || !data, [loading, data])
    const [lastEmotion, setLastEmotion] = useState(null)
    const [processingChat, setProcessingChat] = useState(false)

    const [showAssignActionModal, setShowAssignActionModal] = useState(false)
    const [showTakeActionModal, setShowTakeActionModal] = useState(false)

    const openAssignActionModal = () => setShowAssignActionModal(true)
    const closeAssignActionModal = () => setShowAssignActionModal(false)

    const openTakeActionModal = () => setShowTakeActionModal(true)
    const closeTakeActionModal = () => setShowTakeActionModal(false)

    useEffect(() => {
        setLoading(true)
        loadData()
    }, [studentId])

    const loadData = async () => {
        const resp = await loadStudentWellbeingSchoolYEarData(studentId)
        if (resp.status === 200) {
            setData(resp.data)
            setLastEmotion(resp.data?.feedback)
        } else {
            setData(null)
        }
        setLoading(false)
    }

    const openConversation = async () => {
        setProcessingChat(true)
        const data = {
            member_list: [studentId, user.id],
            type: 'teacher',
        }

        const resp = await createConversation(data)
        if (resp.status === 200) {
            history.push(`/chat?conversationId=${resp.data?.conversation_id}`)
        } else {
        }
        setProcessingChat(false)
    }

    return (
        <div className="mt-[10px]  pb-8 px-8 ">
            <div className="relative">
                <DataContainer data={data} blur={showLoading} />
                {showLoading && <LoadingContainer showEmptyData={!loading && !data} />}
            </div>

            <div className="mt-5" data-tour="student_action_buttons">
                <p className="text-mediumGray text-[16px] leading-5 font-semibold">{t('What u wish to do?')}</p>
                <div className="grid grid-cols-2 gap-[10px] mt-4">
                    <button
                        className={`h-[42px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center gap-1 border border-[#86D3FF33] transition px-1 ${
                            !loading ? 'hover:border-darkenPurple' : ''
                        }`}
                        disabled={loading || (!loading && !data)}
                        onClick={openTakeActionModal}
                        id="take-action-button"
                    >
                        <HeartIcon /> <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('Take Action')}</p>
                    </button>
                    <button
                        className={`h-[42px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center gap-1 border border-[#86D3FF33] transition px-1 ${
                            !loading ? 'hover:border-darkenPurple' : ''
                        }`}
                        disabled={loading || (!loading && !data)}
                        onClick={openAssignActionModal}
                        id="assign-action-button"
                    >
                        <AddPlusCircleIcon /> <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('Assign Action')}</p>
                    </button>
                    <button
                        className={`h-[42px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center gap-1 border border-[#86D3FF33] transition px-1 ${
                            !loading ? 'hover:border-darkenPurple' : ''
                        }`}
                        disabled={loading || (!loading && !data)}
                        onClick={openConversation}
                        id="open-chat-button"
                    >
                        {processingChat ? <Loading /> : <ChatConversationCircleIcon />} <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('Open Chat')}</p>
                    </button>
                    <button
                        className={`h-[42px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center gap-1 border border-[#86D3FF33] transition px-1 ${
                            !loading ? 'hover:border-darkenPurple' : ''
                        }`}
                        disabled={loading || (!loading && !data)}
                        onClick={() => openStudentInfoModal()}
                        id="manage-student-button"
                    >
                        <UserCardIDIcon /> <p className="text-darkenPurple text-[16px] leading-5 font-semibold tracking-tight">{t('Manage Student')}</p>
                    </button>
                </div>
            </div>
            <WellBeingAsignModal modalIsOpen={showAssignActionModal} closeModal={closeAssignActionModal} student={student} note={lastEmotion} />
            <WellBeingTakeActionModal modalIsOpen={showTakeActionModal} closeModal={closeTakeActionModal} student={student} note={lastEmotion} multipleEmotions={false} />
        </div>
    )
}

export default StudentCardAnalytics

const DataContainer = ({ data, blur }) => {
    const { t } = useTranslation()

    const comfortPercentage = useMemo(() => Math.round(data?.comfortablePercentage * 100), [data?.comfortablePercentage])

    return (
        <div className={blur ? 'blur' : ''} data-tour="student_emotional_info">
            <div className="flex flex-row items-center mt-6">
                <div className="flex gap-[20px] flex-col w-full">
                    <div className="text-darkenPurple text-[16px] leading-5 font-semibold flex items-center justify-between w-full">
                        {t('StudentWellbeingThisYear')}
                        <InfoButton
                            text={t(
                                'This section provides an overview of the student’s logged emotions for the current school year. It highlights the total number of emotions recorded, along with the breakdown of comfortable and uncomfortable emotions as percentages.'
                            )}
                        />
                    </div>
                    <div className="flex flex-col gap-[10px]">
                        <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                            {t('TotalEmotions')}: <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.total}</p>
                        </span>

                        <div className="flex flex-row items-center gap-2">
                            <Happy height={32} width={32} />
                            <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                                <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.comfortable}</p>
                                <div className="flex flex-row gap-2">
                                    <p>{t('Comfortable Emotions')}</p>({comfortPercentage}%)
                                </div>
                            </span>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                            <Sad height={32} width={32} />
                            <span className="text-mediumGray text-[16px] leading-5 font-medium flex flex-row gap-1">
                                <p className="text-darkenPurple text-[16px] leading-5 font-bold">{data?.uncomfortable}</p>
                                <div className="flex flex-row gap-2">
                                    <p>{t('Uncomfortable Emotions')}</p>({100 - comfortPercentage}%)
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
